import { appConfig } from '../constants';

/** @type {Map<string, Blob>} */
const blobByFileKey = new Map();
/** @type {Map<string, Blob>} */
const blobByQuestionId = new Map();
/** @type {Map<string, Promise<Blob>>} */
const inflightByFileKey = new Map();

export const getMediaDownloadUrl = (fileKey) =>
  `${appConfig.apiBaseUrl}file/download?action=view&key=${encodeURIComponent(fileKey)}`;

/**
 * Resolve a cached blob by fileKey (preferred) or questionId.
 */
export const getMediaBlob = ({ fileKey, questionId } = {}) => {
  if (fileKey && blobByFileKey.has(fileKey)) {
    return blobByFileKey.get(fileKey);
  }
  if (questionId && blobByQuestionId.has(questionId)) {
    return blobByQuestionId.get(questionId);
  }
  return null;
};

/**
 * Store a blob for the current session (survives question remounts).
 */
export const setMediaBlob = ({ fileKey, questionId, blob } = {}) => {
  if (!blob) return;
  if (fileKey) {
    blobByFileKey.set(fileKey, blob);
  }
  if (questionId) {
    blobByQuestionId.set(questionId, blob);
  }
};

/**
 * Drop cached entries (e.g. after delete & retake).
 */
export const clearMediaBlob = ({ fileKey, questionId } = {}) => {
  if (fileKey) {
    blobByFileKey.delete(fileKey);
    inflightByFileKey.delete(fileKey);
  }
  if (questionId) {
    blobByQuestionId.delete(questionId);
  }
};

export const clearAllMediaBlobs = () => {
  blobByFileKey.clear();
  blobByQuestionId.clear();
  inflightByFileKey.clear();
};

/**
 * Fetch media once per fileKey, write into the session cache, and share in-flight promises.
 */
export const fetchAndCacheMedia = async ({ fileKey, token, questionId } = {}) => {
  if (!fileKey || !token) {
    return null;
  }

  const cached = getMediaBlob({ fileKey, questionId });
  if (cached) {
    // Ensure both keys point at the same blob when we only had one before.
    setMediaBlob({ fileKey, questionId, blob: cached });
    return cached;
  }

  const existing = inflightByFileKey.get(fileKey);
  if (existing) {
    const blob = await existing;
    setMediaBlob({ fileKey, questionId, blob });
    return blob;
  }

  const promise = (async () => {
    const response = await fetch(getMediaDownloadUrl(fileKey), {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch media file (${response.status})`);
    }

    const blob = await response.blob();
    setMediaBlob({ fileKey, questionId, blob });
    return blob;
  })();

  inflightByFileKey.set(fileKey, promise);

  try {
    return await promise;
  } finally {
    if (inflightByFileKey.get(fileKey) === promise) {
      inflightByFileKey.delete(fileKey);
    }
  }
};

/**
 * Fire-and-forget preload — no UI blocking, errors are swallowed.
 */
export const preloadMediaBlob = ({ fileKey, token, questionId } = {}) => {
  if (!fileKey || !token) return;
  if (getMediaBlob({ fileKey, questionId })) return;
  void fetchAndCacheMedia({ fileKey, token, questionId }).catch((error) => {
    console.warn('Media preload failed:', error);
  });
};
